import { luscinia } from "../luscinia";
import { AdvancedFilterManager } from "./AdvancedFilter/advancedFilterManager";
import { BootgridState, GridView, ViewManagerView } from "./ViewManager/viewManagerTypes";
import { ViewManager } from "./ViewManager/ViewManager";
import { constructBootgridFormatters } from "./bootgridFormatters";
import { BootgridSettings } from "./bootgridSettings";
import { BootgridManager } from "./bootgridManager";
import { BootgridChangeType, ColumnFilter, DataType, PageState } from "./bootgridTypes";
import { isRecordPage } from "../Utils/isRecordPage";
import postPageState from "./post-page-state";
import { renderTotalRow } from "./bootgridTotalRow";
import { initializeColumnDragAndDrop } from "./bootgridColumnDragDrop";
import { newRecordHandler, editRecordHandler, deleteRecordHandler, tableExportHandler } from "./bootgridRecordActions";
import { initGridViewManager, constructGridView, stateChangeHandler } from "./bootgridGridView";
import { getColumnSearches, getUrl, submitFilter, getColumnSort } from "./bootgridFilter";
import { initAdvancedFilter, applyAdvancedFilterState } from "./bootgridAdvancedFilter";
import { applySortColumn, initSortColumn } from "./bootgridSorting";
import { applyVisibleColumns } from "./bootgridVisibility";
import { initInlineEditable } from "./bootgridInlineEditable"
import { hasPostValueChange, postValueChange, selectedRowsChanged } from "./bootgridStateManagement"
import { constructCss, constructTemplates, constructLabels } from "./bootgridConstructors";

export class Bootgrid {
    private onNewButtonClick = () => newRecordHandler(this);
    private onEditButtonClick = (e: Event) => {
        const editBtn = (e.currentTarget as HTMLButtonElement);
        editBtn.disabled = true;

        const rowId = editBtn.dataset.rowId ?? this.getSelectedRowIds()[0];
        editRecordHandler(this, rowId).then(() => {
            editBtn.disabled = false;
        });
    };
    private onDeleteButtonClick = async (e: Event) => {
        const currentTarget = (e.currentTarget as HTMLElement);
        if (e instanceof MouseEvent && (e.ctrlKey || e.shiftKey)) {
            return;
        }

        let selectedRows = this.getSelectedRowIds() as string[];
        const targetRowId = currentTarget.dataset.rowId;
        if (targetRowId) {
            if (selectedRows.includes(targetRowId)) {
                e.stopPropagation();            
            } else {
                selectedRows = [targetRowId];
            }
        }

        await deleteRecordHandler(this, selectedRows);
    };        
    private onExportButtonClick = () => tableExportHandler(this);  
    stateChangeHandler = stateChangeHandler.bind(this);
    public async deleteSelectedRowsHandler() {
        let selectedRows = this.getSelectedRowIds();
        
        if (selectedRows.length === 0) {
            luscinia.showNotification(
                luscinia.config.translations.captionNotification, 
                "Please select one or more records to delete.", 
                "warning"
            );
            return;
        }

        await deleteRecordHandler(this, selectedRows);
    }
    public newRecordHandler() {
        newRecordHandler(this);
    }
    public tableExport() {
        tableExportHandler(this);
    }
    public getSelectedRowIds(): string[] {
        return (this.$bootgrid as any).bootgrid("getSelectedRowIds")
    }
    public editRecordHandler(recordId: string): JQuery.jqXHR {
        return editRecordHandler(this, recordId);
    }
    viewManager: ViewManager;
    views: ViewManagerView[];
    advancedFilterManagerContainer: HTMLElement;
    advancedFilterManager: AdvancedFilterManager;
    id: string;
    bootgridManager: BootgridManager;
    bootgridData = luscinia.bootgrid.data;
    config: Config;
    bootgridSettings: BootgridSettings;
    bootgridState: BootgridState;
    $bootgrid: JQuery<HTMLTableElement>;
    tableName: string;
    //since menu item name is unique for each bootgrid in the page this will be used to save and retrieve available views and page view bootgrid view identification.
    menuItemName: string; 
    container: HTMLElement;
    previousRows: string[];
    previousSelectedRowIds: string[];
    constructor(config: Config, bootgrid: any, bootgridState?: BootgridState) {
        this.config = config;
        this.$bootgrid = bootgrid;
        this.id = bootgrid.get(0).id;
        this.tableName = bootgrid.data("luscinia-tablename");
        this.menuItemName = bootgrid.data("menu-item-name");
        this.bootgridSettings = new BootgridSettings(bootgrid, config);
        if (this.bootgridSettings.isParameterBootgrid) {
            throw new Error("You are using wrong initialization for this bootgrid use parameter bootgrid class instead");
        }
        this.bootgridState = bootgridState || this.bootgridSettings.bootgridState;
        this.container = bootgrid[0].closest(".table-container");
    }
    async init(bootgrid?: JQuery<HTMLTableElement>, pageState?: PageState, initialViewId: string = "default", loadDataOnInit: boolean = null): Promise<void> {
            this.incrementLoading();
            const preventInitialLoad =
                loadDataOnInit !== null
                    ? !loadDataOnInit
                    : !this.bootgridSettings.inRecordView && this.$bootgrid.data(luscinia.data.filterColumnName) !== "";
    
            this.$bootgrid = bootgrid || this.$bootgrid;
            this.applyBootgridStateBeforeInit();
            this.initJqueryBootgrid(pageState, false);
            this.addWizardBootgridHeader(this.$bootgrid);
            if (!this.$bootgrid.data("luscinia-advancedsearchdisabled")) {
                initAdvancedFilter.call(this);
            } else if (this.bootgridSettings.addColumnSearch) {
                this.initColumnSearches();
            }
            initGridViewManager.call(this, initialViewId);
            this.applyBootgridStateAfterInit();
            this.addEventListenersAfterInitialize();
    
            if (!preventInitialLoad) {
                await submitFilter.call(this);
            }
            this.decrementLoading();
    }
    getCurrentPage(): string {
        return (this.$bootgrid as any).bootgrid("getCurrentPage");
    }
    ConstructGridView(gridViewName: string): GridView {
        return constructGridView.call(this, gridViewName);
    }
    getDisplayedColumns(): { columnId: string, dataType: DataType }[] {
        const thEls = Array.from(
            this.container.querySelectorAll('table > thead > tr > th[data-column-id]:not([data-column-id="commands"])')
        ) as HTMLElement[];
        return thEls.map((thEl) => {return {columnId: thEl.dataset.columnId, dataType: thEl.dataset.type as DataType}});
    }
    private applyBootgridStateBeforeInit() {
        applyVisibleColumns.call(this);
        this.applyColumnSearch();
        if (this.bootgridState.SortColumn !== null && this.bootgridState.SortColumn.ColumnDataId !== "") {
            applySortColumn.call(this);
        }
    }
    private applyBootgridStateAfterInit() {
        if (!this.$bootgrid.data("luscinia-advancedsearchdisabled")) {
            applyAdvancedFilterState.call(this);
        }
    }
    private applyColumnSearch() {
        if (this.bootgridState.ColumnSearches === undefined || this.bootgridState.ColumnSearches.length === 0) {
            this.bootgridSettings.columnSearchValues = {};
            return;
        }

        let obj = {};
        this.bootgridState.ColumnSearches.forEach((columnSearch) => {
            obj[columnSearch.ColumnDataId] = columnSearch.SearchValue;
        });
        this.bootgridSettings.columnSearchValues = obj;
    }
    private initJqueryBootgrid(pageState: PageState, loadDataOnInit: boolean = null) {
        let isInitial = true;
        (this.$bootgrid as any)
            .on("loaded.rs.jquery.bootgrid", () => {
                this.addHeaderButtonEventListeners();
                this.addDeleteButtonEventListener();
                this.enableDisableEditAndDeleteButtonsBasedOnSelectedRow();
                if (!this.bootgridSettings.inRecordView && !this.isWizardPage()) {
                    if (isInitial && !this.bootgridSettings.allowMultiSelect) {
                        this.applyLastSelectedRow(pageState);
                        isInitial = false;
                    }
                }
                
                // Add open modal event listener to edit buttons
                const rows = this.$bootgrid.find("tbody > tr");
                rows.each((index, element) => {
                    const editBtn = element.querySelector(".command-edit") as HTMLButtonElement;
                    if (editBtn) {
                        editBtn.addEventListener("click", this.onEditButtonClick);
                    }
                }); 

                initInlineEditable.call(this);
                this.$bootgrid.trigger("onloaded", this.bootgridSettings);
            })
            .on("selected.rs.jquery.bootgrid", (e: Event, rows: object[]) => {
                postPageState();
                if (hasPostValueChange.call(this)) {
                    const additionalFormData = this.bootgridSettings.allowMultiSelect
                        ? this.bootgridManager.getPostValueChangeFormData(this, rows, BootgridChangeType.select)
                        : null;
                    postValueChange.call(this, additionalFormData);
                }
            })
            .on("deselected.rs.jquery.bootgrid", (e: Event, rows: object[]) => {
                postPageState();
                if (hasPostValueChange.call(this)) {
                    const additionalFormData = this.bootgridSettings.allowMultiSelect
                        ? this.bootgridManager.getPostValueChangeFormData(this, rows, BootgridChangeType.deselect)
                        : null;
                    postValueChange.call(this, additionalFormData);
                }
            })
            .on("click.rs.jquery.bootgrid", (columns: object[], row: object[]) => {
                this.refreshSubBootgrids();
            })
            .on("afterReloadWithData", (e: Event, bootgrid: any) => {
                if (hasPostValueChange.call(this)) {
                    if (selectedRowsChanged.call(this)) {
                        const additionalFormData = this.bootgridSettings.allowMultiSelect
                            ? this.bootgridManager.getPostValueChangeFormData(this, [], BootgridChangeType.search, bootgrid)
                            : null;
                        postValueChange.call(this, additionalFormData);
                    }
                }
            })
            .on("rowCountChange", async () => {
                await this.reloadTable();
                this.stateChangeHandler();
            })
            .on("pageChange", async () => {
                await this.reloadTable();
                this.stateChangeHandler();
            })
            .on("columnSearchCollapsedChanged", this.stateChangeHandler)
            .on("initialized.rs.jquery.bootgrid", () => {
                this.constructTableUrl();
                this.initColumnSearches();
                initSortColumn.call(this);
                initializeColumnDragAndDrop(this, this.$bootgrid[0]);
            })
            .on("headerRenderedAfterColumnSelectionChanged", () => {
                initializeColumnDragAndDrop(this, this.$bootgrid[0]);
                this.stateChangeHandler();
            })
            .on("stateChanged", this.stateChangeHandler)
            .on("loaded.rs.jquery.bootgrid", () => {
                renderTotalRow(this);
            })
            .bootgrid({
                searchSettings: { delay: 500 },
                selectedRowCount:
                    this.bootgridState.RowDisplayCount === null || isNaN(this.bootgridState.RowDisplayCount)
                        ? 10
                        : this.bootgridState.RowDisplayCount,
                current:
                    this.bootgridState.CurrentPage === null || isNaN(this.bootgridState.CurrentPage) ? 1 : this.bootgridState.CurrentPage,
                twinvisionColumnSearchCollapsed: this.bootgridState.IsColumnSearchCollapsed,
                twinvisionColumnSearchValues: this.bootgridSettings.columnSearchValues,
                rowCount: this.bootgridSettings.paging ? [10, 25, 50, -1] : -1,
                refreshIconVisible: this.bootgridSettings.showRefreshButton,
                columnSelectionVisible: this.bootgridSettings.showColumnSelection,
                selection: this.bootgridSettings.allowSelection,
                multiSelect: true,
                twinvisionColumnSearch: this.bootgridSettings.addColumnSearch,
                twinvisionColumnSearchExclusions: ["commands"],
                rowSelect: this.bootgridSettings.allowRowSelection,
                keepSelection: this.bootgridSettings.allowMultiSelect,
                ajax: this.bootgridSettings.ajax,
                alwaysHaveOneRowSelected: true,
                preventInitialLoad:
                    loadDataOnInit !== null
                        ? !loadDataOnInit
                        : !this.bootgridSettings.inRecordView && this.$bootgrid.data(luscinia.data.filterColumnName) !== "",
                post: () => {
                    let formData = new FormData();
                    if (this.$bootgrid.data("url").startsWith("/parameter")) {
                        formData = luscinia.serializeFormObject((this.$bootgrid as any).closest("form"));
                    }
                    formData.append("id", this.$bootgrid[0].id);
                    return luscinia.objectifyForm(formData);
                },
                url: () => {
                    return getUrl.call(this);
                },
                onColumnSelectionChanged: async () => {
                    this.incrementLoading();
                    const columnSearches = getColumnSearches.call(this);
                    const sortColumn = getColumnSort.call(this);
                    await this.reloadTable();
                    this.initColumnSearches(columnSearches);
                    initSortColumn.call(this, sortColumn);
                    this.decrementLoading();
                },
                onReload: () => this.reloadTable(),
                onRowClicked: () => this.refreshSubBootgrids(),
                css: constructCss.call(this),
                formatters: constructBootgridFormatters(this.$bootgrid, this.bootgridSettings, this.config),
                templates: constructTemplates.call(this),
                labels: constructLabels.call(this),
            });
    }
    loadingCounter: number = 0;
    public incrementLoading() {
      this.loadingCounter++;
      if (this.loadingCounter === 1) {
          this.container.setAttribute("is-loading", "true");
      }
    }

    public decrementLoading() {
      this.loadingCounter = Math.max(0, this.loadingCounter - 1);
      if (this.loadingCounter === 0) {
          this.container.setAttribute("is-loading", "false");
      }
    }
    async reloadTable() {
        this.incrementLoading();
        if (!this.bootgridSettings.allowMultiSelect) {
            const selectedRows: string[] = this.getSelectedRowIds();
            await submitFilter.call(this);
            const currentRows: string[] = (this.$bootgrid as any).bootgrid("getCurrentRowIds");
            if (selectedRows.length === 0 || !currentRows.some(item => selectedRows.includes(item))) {
                this.selectRowByIndex(0);
            }
            else {
                (this.$bootgrid as any).bootgrid("select", selectedRows);
            }
            this.refreshSubBootgrids();
        } else {
            await submitFilter.call(this);
            this.refreshSubBootgrids();
        }
        this.decrementLoading();
    }
    private constructTableUrl() {
        if (this.isMainBootgrid() || isRecordPage()) return;
        
        const mainBootgrid = (this.getMainBootgrid() as any);
        const selectedRows: string[] = mainBootgrid.bootgrid("getSelectedRows");
        const mainBootgridIdentifier: string = mainBootgrid.bootgrid("getIdentifier");
        const baseUrl = this.$bootgrid.data(luscinia.data.baseUrl);
        const filterColumnName = this.$bootgrid.data(luscinia.data.filterColumnName);
        const parentFilterColumnName = this.$bootgrid.data(luscinia.data.parentFilterColumnName);
        const filterValue = selectedRows.map((row) => parentFilterColumnName ? row[parentFilterColumnName] : row[mainBootgridIdentifier]).join(","); 

        this.$bootgrid.get(0).dataset.url = `${baseUrl}/bootgrid/${filterColumnName}?filter-value=${encodeURIComponent(filterValue)}`;
    }
    private refreshSubBootgrids() {
        if (!this.isMainBootgrid()) {
            return;
        }
        const selectedRows: string[] = (this.$bootgrid as any).bootgrid("getSelectedRows");
        if (selectedRows.length > 0) {
            const mainBootgridIdentifier: string = (this.$bootgrid as any).bootgrid("getIdentifier");
            this.getSubBootgrids().each((index, element) => {
                const $currentBootgrid = $(element);
                const baseUrl = $currentBootgrid.data(luscinia.data.baseUrl);
                const filterColumnName = $currentBootgrid.data(luscinia.data.filterColumnName);
                const parentFilterColumnName = $currentBootgrid.data(luscinia.data.parentFilterColumnName);
                const filterValue = selectedRows.map((row) => parentFilterColumnName ? row[parentFilterColumnName] : row[mainBootgridIdentifier]).join(","); 
                element.dataset.url = `${baseUrl}/bootgrid/${filterColumnName}?filter-value=${encodeURIComponent(filterValue)}`;
    
                ($currentBootgrid as any).bootgrid("reload");
            });
        } else {
            this.getSubBootgrids().each((index, element) => {
                element.dataset.url = "";
                const $currentBootgrid = $(element);
                ($currentBootgrid as any).bootgrid("reload");
            });
        }
    }
    private getSubBootgrids() {
        return $(`.${luscinia.css.bootgrid}[data-luscinia-filtercolumnname]:not([data-luscinia-filtercolumnname=""])`);
    }
    private getMainBootgrid() {
        return $(`.${luscinia.css.bootgrid}[data-luscinia-filtercolumnname=""]`);
    }
    public isMainBootgrid() {
        return this.$bootgrid.data(luscinia.data.filterColumnName) === "";
    }
    // data-filter-widget wordt door jquery.bootgrid.js NIET overgenomen wanneer het de headerrij
    // zelf opnieuw opbouwt (alleen bekende velden als data-column-id blijven staan) - daarom hier
    // ophalen via de column-settings (die wel uit de oorspronkelijke data-* attributen zijn
    // opgebouwd bij het inladen van de kolommen).
    getColumnFilterWidget(columnId: string): string {
        const column = (this.$bootgrid as any).bootgrid("getColumnSettings").find((c) => c.id === columnId);
        return column?.filterWidget;
    }
    private getColumnFilterOptionsUrl(columnId: string): string {
        const column = (this.$bootgrid as any).bootgrid("getColumnSettings").find((c) => c.id === columnId);
        return column?.filterOptionsUrl;
    }
    // Vervangt de standaard tekst-zoekbalk onder de kolomkop door een readonly veld dat, bij klikken,
    // een checkbox-dropdown toont (dezelfde "includes_any"-waarden als het geavanceerde filter, maar
    // dan direct bereikbaar boven de grid). jquery.bootgrid.js bouwt dit tekstveld telkens opnieuw op
    // bij initialisatie, dus dit wordt per initColumnSearches-aanroep opnieuw toegepast.
    private initCheckboxMultiSelectColumnSearch(columnId: string, inputEl: HTMLInputElement, columnFilters: ColumnFilter[]) {
        const columnFilter = columnFilters?.find((filter) => filter.ColumnName === columnId);
        if (columnFilter) {
            inputEl.value = columnFilter.Values.map((v) => v === "" ? "__empty__" : v).join(",");
        }
        inputEl.readOnly = true;
        inputEl.classList.add("checkbox-multiselect-column-search");

        if (inputEl.dataset.checkboxMultiselectInitialized === "true") return;
        inputEl.dataset.checkboxMultiselectInitialized = "true";

        const optionsUrl = this.getColumnFilterOptionsUrl(columnId);
        let panel: HTMLElement = null;

        const closePanel = () => {
            panel?.remove();
            panel = null;
        };

        const openPanel = () => {
            panel = document.createElement("div");
            panel.classList.add("checkbox-multiselect-column-search-panel", "shadow-sm");
            panel.style.position = "absolute";
            panel.style.zIndex = "1050";
            panel.style.background = "#fff";
            panel.style.border = "1px solid #ced4da";
            panel.style.borderRadius = "4px";
            panel.style.maxHeight = "220px";
            panel.style.overflowY = "auto";
            panel.style.minWidth = Math.max(inputEl.offsetWidth, 160) + "px";
            panel.style.padding = "4px";
            panel.innerHTML = '<div class="text-muted small p-1">Laden...</div>';

            const rect = inputEl.getBoundingClientRect();
            panel.style.top = window.scrollY + rect.bottom + "px";
            panel.style.left = window.scrollX + rect.left + "px";
            document.body.appendChild(panel);

            if (!optionsUrl) {
                panel.innerHTML = '<div class="text-danger small p-1">Geen opties-url geconfigureerd</div>';
                return;
            }

            fetch(optionsUrl)
                .then((r) => r.json())
                .then((options: string[]) => {
                    if (!panel) return;
                    panel.innerHTML = "";
                    const selected = inputEl.value ? inputEl.value.split(",") : [];

                    const addRow = (value: string, label: string) => {
                        const wrap = document.createElement("div");
                        wrap.classList.add("form-check");

                        const checkboxId = "colsearch-cb-" + columnId + "-" + Math.random().toString(36).slice(2);
                        const cb = document.createElement("input");
                        cb.type = "checkbox";
                        cb.classList.add("form-check-input");
                        cb.id = checkboxId;
                        cb.value = value;
                        if (selected.includes(value)) cb.checked = true;
                        cb.addEventListener("change", async () => {
                            const checked = Array.from(panel.querySelectorAll("input[type=checkbox]:checked")) as HTMLInputElement[];
                            inputEl.value = checked.map((c) => c.value).join(",");
                            await this.reloadTable();
                            this.stateChangeHandler();
                        });

                        const lbl = document.createElement("label");
                        lbl.classList.add("form-check-label");
                        lbl.setAttribute("for", checkboxId);
                        lbl.textContent = label;

                        wrap.appendChild(cb);
                        wrap.appendChild(lbl);
                        panel.appendChild(wrap);
                    };

                    addRow("__empty__", "(leeg)");
                    options.forEach((optionValue) => addRow(optionValue, optionValue));
                })
                .catch(() => {
                    if (panel) panel.innerHTML = '<div class="text-danger small p-1">Kon opties niet laden</div>';
                });
        };

        inputEl.addEventListener("click", (e) => {
            e.stopPropagation();
            if (panel) {
                closePanel();
            } else {
                openPanel();
            }
        });

        document.addEventListener("click", (e) => {
            if (panel && !panel.contains(e.target as Node) && e.target !== inputEl) {
                closePanel();
            }
        });
    }
    initColumnSearches(columnFilters: ColumnFilter[] = null) {
        const tableHeaders = this.container.querySelectorAll('table > thead > tr > th[data-column-id]:not([data-column-id="commands"])');
        tableHeaders.forEach((header) => {
            const columnId = header.getAttribute("data-column-id");
            const inputEl = header.querySelector("input");
            if (inputEl && this.getColumnFilterWidget(columnId) === "checkbox-multiselect") {
                this.initCheckboxMultiSelectColumnSearch(columnId, inputEl as HTMLInputElement, columnFilters);
                return;
            }
            if (inputEl) {
                const columnFilter = columnFilters?.find((filter) => filter.ColumnName === columnId);
                if (columnFilter) {
                    inputEl.value = columnFilter.Values[0];
                }
                const columnSearchHandler = this.debounce (async(e: KeyboardEvent) => {
                    if (
                        e.key === "Enter" ||
                        e.key === "Escape" ||
                        e.key === "Tab" ||
                        e.key === "Shift" ||
                        e.key === "Control" ||
                        e.key === "Alt" ||
                        e.key === "CapsLock"
                    ) {
                        this.decrementLoading();
                        return;
                    }
                    await this.reloadTable();
                    this.stateChangeHandler();
                    this.decrementLoading();
                }, 800);

                inputEl.removeEventListener("keyup", columnSearchHandler);
                inputEl.addEventListener("keyup", columnSearchHandler);

                inputEl.addEventListener("keypress", (event: KeyboardEvent) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                    }
                });
            }
        });

        const constructClearButton = () => {
            const actionButtonsContainer = this.container.querySelector(".actions.btn-group") as HTMLElement;
            
            if(actionButtonsContainer.querySelector(".clear-filters-button")) return;

            const btn = document.createElement("button");
            btn.classList.add("clear-filters-button");
            btn.addEventListener("click", (e: MouseEvent) => {
                e.preventDefault();
                this.clearColumnFilters();
            });
            btn.classList.add("btn", "btn-outline-secondary");
            btn.setAttribute("type", "button");


            const trashIcon = document.createElement("i");
            trashIcon.classList.add("fa", "fa-trash");

            btn.appendChild(trashIcon);
            // btn.appendChild(crossIcon);
            actionButtonsContainer.appendChild(btn);
        };

        constructClearButton();
    }
    debounceLoadingActive: boolean = false;
    private debounce(func: Function, wait: number) {
        let timeout: number | undefined;
        return (...args: any[]) => {
            if (!this.debounceLoadingActive) {
                this.debounceLoadingActive = true;
                this.incrementLoading();
            }
            clearTimeout(timeout);
            timeout = window.setTimeout(() => {
                func.apply(this, args);
                this.debounceLoadingActive = false;
            }, wait);
        };
    }
    private async clearColumnFilters() {
        const tableHeaders = this.container.querySelectorAll('table > thead > tr > th[data-column-id]:not([data-column-id="commands"])');
        tableHeaders.forEach((header) => {
            const inputEl = header.querySelector("input");
            if (inputEl) {
                inputEl.value = "";
                
            }
        });

        const generalSearchInput = (this.container?.querySelector("input.search-field") as HTMLInputElement);
        if (generalSearchInput) {
            generalSearchInput.value = "";
        }
        
        await this.reloadTable();
        this.stateChangeHandler();
    }
    private addHeaderButtonEventListeners() {
        const newButton = this.container?.querySelector("[data-" + luscinia.data.action + "='record-new']");
        const editButton = this.container?.querySelector("[data-" + luscinia.data.action + "='record-edit']");
        const deleteButton = this.container?.querySelector("[data-" + luscinia.data.action + "='record-delete']");
        const exportButton = this.container?.querySelector("[data-" + luscinia.data.action + "='table-export']");

        newButton?.removeEventListener("click", this.onNewButtonClick);
        editButton?.removeEventListener("click", this.onEditButtonClick);
        deleteButton?.removeEventListener("click", this.onDeleteButtonClick);
        exportButton?.removeEventListener("click", this.onExportButtonClick);

        newButton?.addEventListener("click", this.onNewButtonClick);
        editButton?.addEventListener("click", this.onEditButtonClick);
        deleteButton?.addEventListener("click", this.onDeleteButtonClick);
        exportButton?.addEventListener("click", this.onExportButtonClick);
    }
    getSelectedRowCount(): string {
        let count = "-1";
        const rowCountDropdown = this.container.querySelector("#rowcount-dropdown") as HTMLElement;
        if (rowCountDropdown) {
            count = rowCountDropdown.querySelector("li.active > a").getAttribute("href").replace("#", "");
        }
        return count;
    }
    destroy() {
        (this.$bootgrid as any).bootgrid("destroy");
        this.advancedFilterManager.destroy();
    }
    async reRender() {
        this.destroy();
        await this.init($(`#${this.id}`), undefined, this.viewManager.getSelectedViewId(), true);
    }
    private addWizardBootgridHeader(bootgrid: JQuery<HTMLElement>) {
        if ((bootgrid.data("url") as string).includes(".Wizards.") && $("#bootgridHeader").length === 0) {
            const bootgridHeader = this.tableName;
            let h2 = document.createElement("h2");
            h2.id = "bootgridHeader";
            let text = document.createTextNode(bootgridHeader);
            h2.appendChild(text);
            bootgrid[0].previousSibling.insertBefore(h2, bootgrid[0].previousSibling.firstChild);
        }
    }
    private async applyLastSelectedRow(pageState: PageState) {
        if (pageState === undefined || pageState === null || pageState.selectedRows === null) {
            this.selectRowByIndex(0);
            this.refreshSubBootgrids();
            return;
        }
        const selectedRowId = pageState.selectedRows[this.menuItemName];
        if (selectedRowId === undefined || selectedRowId === null || selectedRowId === "" || !this.rowAvailable(selectedRowId)) {
            this.selectRowByIndex(0);
        } else {
            (this.$bootgrid as any).bootgrid("select", [selectedRowId]);
        }
        this.refreshSubBootgrids();
    }
    private rowAvailable(rowId: string): boolean {
        return this.$bootgrid.find(`tr[data-row-id='${rowId}']`).length > 0;
    }
    public selectRowByIndex(index: number) {
        const firstRowId = this.$bootgrid.find("tbody > tr").eq(index).data("row-id");
        if (firstRowId === undefined || firstRowId === null || firstRowId === "") return false;
        (this.$bootgrid as any).bootgrid("select", [firstRowId]);
    }
    private enableDisableEditAndDeleteButtonsBasedOnSelectedRow() {
        let selectedRow = this.$bootgrid.find("tr." + luscinia.css.rowSelected);
        var id = selectedRow.data("row-id");
        if (id === undefined || id === "") {
            this.$bootgrid
                .parent()
                .find(`[${luscinia.css.dataTableSelectionChanged}][data-${luscinia.data.action}='record-edit']`)
                .attr("disabled", "");
            this.$bootgrid
                .parent()
                .find(`[${luscinia.css.dataTableSelectionChanged}][data-${luscinia.data.action}='record-delete']`)
                .attr("disabled", "");
        } else {
            this.$bootgrid
                .parent()
                .find(`[${luscinia.css.dataTableSelectionChanged}][data-${luscinia.data.action}='record-edit']`)
                .removeAttr("disabled");
            this.$bootgrid
                .parent()
                .find(`[${luscinia.css.dataTableSelectionChanged}][data-${luscinia.data.action}='record-delete']`)
                .removeAttr("disabled");
        }
        if (!this.bootgridSettings.showHeaderInfoBar) {
            this.$bootgrid.closest(".form-group").find(".bootgrid-header").hide();
        }
        if (!this.bootgridSettings.showFooterInfoBar) {
            this.$bootgrid.closest(".form-group").find(".bootgrid-footer").hide();
        }
    }
    private addDeleteButtonEventListener() {
        this.$bootgrid
            .get(0)
            .querySelectorAll(".command-delete")
            .forEach(btn => btn.addEventListener("click", this.onDeleteButtonClick));
    }
    private addEventListenersAfterInitialize() {
        const tbody = this.$bootgrid.find("tbody");
        const handleDoubleClick = () => {
            if (tbody.find("tr .no-results").length > 0) return;
            tbody.off("dblclick");

            const selectedRowIds = this.getSelectedRowIds();
            if (selectedRowIds.length > 0) {
                editRecordHandler(this, selectedRowIds[0]).then(() => {
                    setTimeout(() => {
                        tbody.on("dblclick", handleDoubleClick);
                    }, 500);
                });
            } else {
                tbody.on("dblclick", handleDoubleClick);
            }
        };

        tbody.on("dblclick", handleDoubleClick);
    }
    public isWizardPage(): boolean {
        return $("#wizardcontainer").length > 0;
    }    
}